import { useEffect, useMemo, useState } from "react";
import { Plane, Plus, Trash2, Link2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { SearchPick } from "@/components/SearchPick";
import { LEGS, EMPTY_SEG, loadAirports, loadAirlines, airportLabel, logoUrl } from "@/lib/flights";

const F = ({ label, error, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</Label>
    {children}
    {error && <p className="text-xs font-medium text-rose-600">{error}</p>}
  </div>
);

const AirlineItem = ({ a }) => (
  <span className="flex items-center gap-2">
    {a.has_logo ? <img src={logoUrl(a.code)} alt="" className="h-5 w-8 object-contain" /> : <span className="w-8 text-center font-mono text-xs text-slate-400">{a.code}</span>}
    <span className="truncate">{a.name}</span>
  </span>
);

const Segment = ({ seg, onChange, onRemove, airports, airlines, err, tid, title }) => {
  const set = (k) => (v) => onChange({ ...seg, [k]: typeof v === "string" ? v : v.target.value });
  const ap = (k) => (
    <SearchPick items={airports} value={seg[k]} onChange={set(k)} placeholder="Busca ciudad, aeropuerto o código" testid={`${tid}-${k}`}
      label={(it) => it.label} search={(it) => it.text} error={err(k)} />
  );
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4" data-testid={tid}>
      <div className="mb-3 flex items-center justify-between">
        <p className="text-sm font-semibold text-slate-800">{title}</p>
        <Button type="button" variant="ghost" size="sm" className="text-rose-600" onClick={onRemove} data-testid={`${tid}-remove`}><Trash2 size={14} className="mr-1" />Quitar</Button>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <F label="Salida" error={err("from_code")}>{ap("from_code")}</F>
        <F label="Llegada" error={err("to_code")}>{ap("to_code")}</F>
        <F label="Aerolínea" error={err("airline")}>
          <SearchPick items={airlines} value={seg.airline} onChange={set("airline")} placeholder="Busca la aerolínea" testid={`${tid}-airline`}
            label={(it) => it.name} search={(it) => `${it.code} ${it.name} ${it.country}`} error={err("airline")} />
        </F>
        <F label="Número de vuelo" error={err("flight_no")}><Input value={seg.flight_no} onChange={set("flight_no")} placeholder="Ej. AM 523" maxLength={10} data-testid={`${tid}-flight-no`} /></F>
        <F label="Fecha de salida" error={err("dep_date")}><Input type="date" value={seg.dep_date} onChange={set("dep_date")} data-testid={`${tid}-dep-date`} /></F>
        <F label="Hora de salida" error={err("dep_time")}><Input type="time" value={seg.dep_time} onChange={set("dep_time")} data-testid={`${tid}-dep-time`} /></F>
        <F label="Fecha de llegada" error={err("arr_date")}><Input type="date" value={seg.arr_date} min={seg.dep_date || undefined} onChange={set("arr_date")} data-testid={`${tid}-arr-date`} /></F>
        <F label="Hora de llegada" error={err("arr_time")}><Input type="time" value={seg.arr_time} onChange={set("arr_time")} data-testid={`${tid}-arr-time`} /></F>
      </div>
    </div>
  );
};

const Leg = ({ leg, title, segs, setSegs, lists, errors }) => {
  const add = () => setSegs([...segs, { ...EMPTY_SEG, from_code: segs.length ? segs[segs.length - 1].to_code : "" }]);
  return (
    <div className="space-y-3" data-testid={`flights-${leg}`}>
      <p className="flex items-center gap-2 text-sm font-semibold text-slate-900"><Plane size={16} className={leg === "inbound" ? "rotate-180" : ""} />{title}</p>
      {segs.length === 0 && <Button type="button" variant="outline" size="sm" onClick={add} data-testid={`flights-${leg}-add`}><Plus size={15} className="mr-1.5" />Agregar {title.toLowerCase()}</Button>}
      {segs.map((s, i) => (
        <div key={i} className="space-y-3">
          <Segment seg={s} {...lists} tid={`flight-${leg}-${i}`} title={i === 0 ? title : `Conexión ${i}`}
            err={(k) => errors[`flights.${leg}.${i}.${k}`]}
            onChange={(v) => setSegs(segs.map((x, j) => (j === i ? v : x)))} onRemove={() => setSegs(segs.filter((_, j) => j !== i))} />
          {i === segs.length - 1 && segs.length < 4 && (
            <Button type="button" variant="ghost" size="sm" className="text-[#2F5BFF]" onClick={add} data-testid={`flights-${leg}-add-connection`}><Link2 size={15} className="mr-1.5" />Agregar conexión</Button>
          )}
        </div>
      ))}
    </div>
  );
};

export const FlightsSection = ({ value, onChange, errors }) => {
  const [raw, setRaw] = useState({ airports: [], airlines: [] });
  useEffect(() => {
    Promise.all([loadAirports(), loadAirlines()]).then(([a, l]) => setRaw({ airports: a, airlines: l })).catch(() => {});
  }, []);
  const lists = useMemo(() => ({
    airports: raw.airports.map((a) => ({ key: a[0], label: airportLabel(a), text: `${a[0]} ${a[1]} ${a[2]} ${a[3]}`, node: <span className="truncate"><b className="font-mono">{a[0]}</b> · {a[2] || a[1]} <span className="text-slate-400">· {a[1]} · {a[3]}</span></span> })),
    airlines: raw.airlines.map((a) => ({ ...a, key: a.code, node: <AirlineItem a={a} /> })),
  }), [raw]);
  const f = value || { outbound: [], inbound: [] };
  return (
    <fieldset className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-7" data-testid="flights-section">
      <legend className="px-2 font-heading text-base font-semibold text-slate-900">Vuelos <span className="text-sm font-normal text-slate-400">(opcional)</span></legend>
      <p className="mb-5 text-xs text-slate-500">Llena solo la ida, solo el regreso o ambos. Si hay escala usa “Agregar conexión”.</p>
      <div className="space-y-6">
        {LEGS.map(([leg, title]) => (
          <Leg key={leg} leg={leg} title={title} segs={f[leg] || []} lists={lists} errors={errors}
            setSegs={(segs) => onChange({ ...f, [leg]: segs })} />
        ))}
      </div>
    </fieldset>
  );
};
